import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { STAGE_LABEL, isStage, listsAsOverdue, type Stage } from '@/domain';
import { useMyQueue } from '@/application/usecases/useKits';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { useBoard } from '@/application/usecases/useBoard';
import { formatDuration, useNow } from '@/application/timer/timerStore';
import { initials } from '@/shared/format/initials';
import { strings } from '@/shared/strings';
import { labToday } from '@/shared/format/instant';
import { GoBackIcon } from '@/ui/icons';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { StageKitTable, type StageFocus } from './StageKitTable';
import { ListFreshness } from '@/features/shell/ListFreshness';
import styles from './StageScreen.module.css';

/**
 * Stage detail, reached by tapping a stage on the production line.
 *
 * The kit list comes from `GET /kits?stage=`, and the live work chips come from
 * the board, because who is timing a tray right now is not something a kit
 * carries. Two queries rather than one, but the board is already warm from the
 * line the user just tapped.
 */
export function StageScreen() {
  const { stage: raw } = useParams();
  const navigate = useNavigate();

  const stage: Stage | null = raw !== undefined && isStage(raw) ? raw : null;

  /*
   * A page at a time, from the server. Section 10 of the request of
   * 16 September 2026: a bench with three hundred trays behind it lost a
   * hundred of them off the end of this list.
   */
  const paging = useTablePage('stage');
  // An origin sort, which the server orders the whole list by. Section 2 of the
  // request of 24 September 2026. The table says which, as the reader chooses.
  const [sort, setSort] = useState<string | undefined>(undefined);
  // The figure last pressed, which the table is narrowed by. The lab's request of 17 October 2026, item 1.
  const [focus, setFocus] = useState<StageFocus | null>(null);
  const press = (show: StageFocus['show']) => setFocus({ show, at: Date.now() });
  const kits = useMyQueue(
    useMemo(
      () =>
        stage === null
          ? {}
          : {
              stage,
              page: paging.page,
              pageSize: paging.pageSize,
              ...(sort === undefined ? {} : { sort }),
            },
      [stage, paging.page, paging.pageSize, sort],
    ),
  );
  const board = useBoard();

  const rows = useMemo(() => kits.data?.items ?? [], [kits.data]);
  /*
   * Every tray at this bench, not the page. The heading and the load figure
   * answer "how busy is this bench", which a page of twenty-five cannot.
   */
  const total = kits.data?.total ?? 0;
  const working = (board.data?.liveWork ?? []).filter((entry) => entry.stage === stage);

  if (stage === null) {
    // A hand typed or stale stage in the URL. The line is the honest place to
    // send somebody, not an error page.
    return (
      <div>
        <BackLink onClick={() => void navigate('/line')} />
        <div className={styles.empty}>
          <h3>{strings.stage.empty}</h3>
          <p>{strings.stage.emptyDetail}</p>
        </div>
      </div>
    );
  }

  // The lists' one rule, so this figure and the Overdue only filter agree.
  const today = labToday();
  const overdue = rows.filter((kit) => listsAsOverdue(kit, today)).length;
  const oldest = rows.reduce((most, kit) => Math.max(most, kit.minutesInStage), 0);
  /** Said out loud only while there is more than one page to be wrong about. */
  const pageScope = total > rows.length ? strings.table.onThisPage : null;

  return (
    <div>
      <BackLink onClick={() => void navigate('/line')} />

      <div className={styles.head}>
        <span className={styles.lbl}>{strings.stage.label}</span>
        <h2>{STAGE_LABEL[stage]}</h2>
        <span className={styles.hint}>
          {total} {total === 1 ? strings.stage.kit : strings.stage.kits}
        </span>
        <span className={styles.spacer} />
        <ListFreshness query={kits} />
      </div>

      {kits.isError && <p className={styles.hint}>{strings.errors.server}</p>}

      {rows.length > 0 && (
        <div className={styles.stats}>
          <Stat
            label={strings.stage.statKits}
            value={String(total)}
            onPress={() => press('all')}
            pressed={focus?.show === 'all'}
          />
          <Stat
            label={strings.stage.statOverdue}
            value={String(overdue)}
            alert={overdue > 0}
            note={pageScope}
            onPress={() => press('overdue')}
            pressed={focus?.show === 'overdue'}
          />
          <Stat
            label={strings.stage.statOldest}
            value={formatDuration(oldest * 60)}
            note={pageScope}
            onPress={() => press('oldest')}
            pressed={focus?.show === 'oldest'}
          />
        </div>
      )}

      {working.length > 0 && (
        <>
          <div className={styles.count}>{strings.stage.working}</div>
          <div className={styles.chips}>
            {working.map((entry) => (
              <WorkingChip key={entry.userId} name={entry.userName} startedAt={entry.startedAt} />
            ))}
          </div>
        </>
      )}

      {/*
        The table's own empty state says the stage is clear, which is a
        different statement from "the kits have not arrived yet" and the wrong
        one to make while the query is still in flight.
      */}
      {kits.isPending ? (
        <SkeletonRows rows={5} label={strings.common.loadingBoard} />
      ) : (
        <StageKitTable
          kits={rows}
          tableKey={stage}
          layoutKey="stage"
          onRowClick={(kit) => void navigate(`/queue/${kit.id}`)}
          caption={STAGE_LABEL[stage]}
          emptyHeading={strings.stage.empty}
          emptyDetail={strings.stage.emptyDetail}
          onListChanged={paging.reset}
          onSortParamChange={setSort}
          focus={focus}
          selectable
          paging={{
            page: paging.page,
            pageSize: paging.pageSize,
            total: kits.data?.total ?? 0,
            sizes: PAGE_SIZES,
            onPage: paging.goTo,
            onPageSize: paging.choose,
            label: strings.table.pagesLabel,
          }}
        />
      )}
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button className={styles.back} onClick={onClick}>
      <GoBackIcon />
      {strings.stage.back}
    </button>
  );
}

function Stat({
  label,
  value,
  alert = false,
  note,
  onPress,
  pressed = false,
}: {
  label: string;
  value: string;
  alert?: boolean;
  /**
   * What the figure is of, when it is not of everything. A count of the page
   * that does not say so is exactly the quiet lie section 10 of the request of
   * 16 September 2026 exists to fix.
   */
  note?: string | null;
  /** Narrows the table to what the figure counts: the lab's request of 17 October 2026, item 1. */
  onPress: () => void;
  pressed?: boolean;
}) {
  return (
    <button type="button" className={`${styles.stat} ${styles.statPress}`} aria-pressed={pressed} onClick={onPress}>
      <div className={styles.l}>{label}</div>
      <div className={`${styles.v} ${alert ? styles.alert : ''}`}>{value}</div>
      {note != null && <div className={styles.scope}>{note}</div>}
    </button>
  );
}

/**
 * Reads its elapsed time from the one application ticker, section 10 item 4.
 * A chip per technician with its own interval is exactly the thing that rule
 * exists to prevent.
 */
function WorkingChip({ name, startedAt }: { name: string; startedAt: string }) {
  const now = useNow();
  const seconds = Math.max(0, Math.floor((now - Date.parse(startedAt)) / 1000));

  return (
    <span className={styles.chip}>
      <span className={styles.avatar}>{initials(name)}</span>
      {name.split(' ')[0]}
      <span className={styles.elapsed}>{formatDuration(seconds)}</span>
    </span>
  );
}
